import React from 'react';
import { motion } from 'framer-motion';
import { Mountain, CheckCircle, ArrowRight, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

const MiningIndustryPage: React.FC = () => {
  const systems = [
    {
      title: 'Self-Cleaning Suction Screens',
      description: 'Surface or pit-water intakes for remote-site conditions',
      link: '/filtration/suction-screens/self-cleaning'
    },
    {
      title: 'Inline Pressure Filters',
      description: 'Slurry dilution, washdown, and dust suppression filtration',
      link: '/filtration/inline/big'
    },
    {
      title: 'Standard Suction Screens',
      description: 'Heavy-duty intake protection for dewatering pumps',
      link: '/filtration/suction-screens/standard'
    }
  ];

  const benefits = [
    'Protect pumps, spray systems, and dewatering equipment',
    'Withstand abrasive solids and fluctuating flows',
    'Stainless-steel construction for remote-site durability',
    'Low-maintenance, self-cleaning options for continuous operation',
    'Custom mesh sizes for specific particle removal requirements',
    'Rated for high-pressure and high-solids environments'
  ];

  const applications = [
    {
      title: 'Mine Dewatering',
      description: 'Protect dewatering pumps from debris and solids in pit water and underground operations.'
    },
    {
      title: 'Process Water Loops',
      description: 'Filter recirculated water for ore processing, flotation, and mineral separation systems.'
    },
    {
      title: 'Aggregate Washing',
      description: 'Handle high-solids washdown water in sand, gravel, and crushed stone operations.'
    },
    {
      title: 'Tailings Pond Recirculation',
      description: 'Maintain pump protection and system reliability in abrasive tailings management applications.'
    }
  ];

  return (
    <>
      <SEOHead
        title="Mining & Quarrying Filtration Systems | CTC Waterworks"
        description="Stainless steel filters and suction screens for mining, quarry, and aggregate water systems. Built to handle abrasive, high-solids water."
        canonicalPath="/industries/mining"
      />
      <div className="min-h-screen bg-gray-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-gray-700 to-gray-900 text-white py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mr-4">
                    <Mountain className="text-white" size={32} />
                  </div>
                  <h1 className="text-5xl font-bold">
                    Mining & Quarrying
                  </h1>
                </div>
                <p className="text-2xl text-gray-300 mb-6 font-light leading-relaxed">
                  Heavy-duty water systems demand rugged, low-maintenance filtration. CTC stainless-steel filters and suction screens withstand abrasive solids, fluctuating flows, and remote-site conditions common in mining and aggregate operations.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="text-green-400 mr-3 mt-1 flex-shrink-0" size={24} />
                    <span className="text-gray-200">Protect pumps, spray systems, and dewatering equipment</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-green-400 mr-3 mt-1 flex-shrink-0" size={24} />
                    <span className="text-gray-200">Self-cleaning suction screens for surface or pit-water intakes</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-green-400 mr-3 mt-1 flex-shrink-0" size={24} />
                    <span className="text-gray-200">Inline pressure filters for slurry dilution, washdown, and dust suppression</span>
                  </li>
                </ul>
              </motion.div>

              <div className="bg-white rounded-lg shadow-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Get Expert Help</h3>
                <Link
                  to="/contact?topic=mining-filtration"
                  className="block w-full bg-gray-800 text-white px-6 py-4 rounded-lg font-semibold hover:bg-gray-900 transition-colors text-center shadow-lg mb-4"
                >
                  <Mail className="inline-block mr-2 mb-1" size={20} />
                  Talk to a Filtration Expert
                </Link>
                <p className="text-sm text-gray-600 text-center">
                  Get design assistance, submittals, and spec packs for your next project.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Applications Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Mining & Quarrying Applications
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {applications.map((app, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-gray-50 rounded-lg p-6 border border-gray-200 hover:border-gray-800 hover:shadow-lg transition-all"
                >
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{app.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{app.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Systems Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Recommended Filtration Systems
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {systems.map((system, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link
                    to={system.link}
                    className="block bg-white rounded-lg shadow-lg p-6 h-full hover:shadow-xl transition-all border-2 border-transparent hover:border-gray-800 group"
                  >
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-gray-800">
                      {system.title}
                    </h3>
                    <p className="text-gray-700 mb-4 leading-relaxed">{system.description}</p>
                    <span className="inline-flex items-center text-gray-800 font-semibold">
                      Learn More <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Why CTC for Mining Operations
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-start"
                >
                  <CheckCircle className="text-gray-800 mr-3 mt-1 flex-shrink-0" size={24} />
                  <span className="text-gray-700 text-lg">{benefit}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-br from-gray-800 to-gray-900 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-4">Let's Engineer Your Solution</h2>
            <p className="text-xl text-gray-300 mb-8">
              From mine dewatering to aggregate washing, our systems are built to perform in the toughest conditions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact?topic=mining-filtration"
                className="inline-flex items-center justify-center bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
              >
                <span>Talk to a Filtration Expert</span>
                <ArrowRight size={20} className="ml-2" />
              </Link>
              <a
                href="tel:+15099017476"
                className="inline-flex items-center justify-center bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors"
              >
                <span>(509) 901-7476</span>
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default MiningIndustryPage;
